
import { useState, useEffect, useRef, useMemo } from 'react';
import { Tooltip } from './Tooltip';
import { AudioPlayerOverlay } from './AudioPlayerOverlay';

// ==========================================
// CENTRAL HELPER
// ==========================================
const adminFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('dlk_admin_token') || '';
    const headers = new Headers(options.headers || {});
    if (token) headers.set('X-Admin-Token', token.trim());

    // Auto-JSON content type
    if (options.body && typeof options.body === 'string' && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    return await fetch(url, { ...options, headers });
};

const formatError = async (r: Response) => {
    const type = r.headers.get("Content-Type") || "";
    if (type.includes("application/json")) {
        try { const j = await r.json(); return `(${r.status}) ${j.error || j.message || 'Error'} [${j.code || ''}]`; } catch { }
    }
    const txt = await r.text();
    return `(${r.status}) ${txt.slice(0, 150)}`;
};

// ==========================================
// MAIN FACTORY COMPONENT
// ==========================================
export const AdminFactory = () => {
    const [token, setToken] = useState(() => localStorage.getItem('dlk_admin_token') || '');
    const [isAdmin, setIsAdmin] = useState(false);
    const [status, setStatus] = useState('');
    const [activeTab, setActiveTab] = useState<'pipeline' | 'manifest' | 'queue' | 'maintain'>('pipeline');
    const [authCheckTime, setAuthCheckTime] = useState<string>('');

    const validateToken = async () => {
        try {
            const r = await adminFetch('/api/factory/whoami');
            const d = await r.json();
            if (d.ok && d.isAdmin) {
                setIsAdmin(true);
                localStorage.setItem('dlk_admin_token', token);
                setAuthCheckTime(new Date().toLocaleTimeString());
            } else { setIsAdmin(false); setStatus('Invalid token'); }
        } catch (e) { setStatus(`Auth Error: ${e}`); }
    };
    useEffect(() => { if (token) validateToken(); }, []);

    if (!isAdmin) {
        return (
            <div style={{ padding: '40px', color: '#ccc' }}>
                <h2>ðŸ­ Factory (Restricted)</h2>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <input type="password" value={token} onChange={e => {
                        setToken(e.target.value);
                        localStorage.setItem('dlk_admin_token', e.target.value);
                    }} placeholder="Admin Token" style={{ padding: '10px' }} />
                    <button onClick={validateToken} style={{ padding: '10px' }}>Login</button>
                </div>
                {status && <div style={{ color: 'red', marginTop: '10px' }}>{status}</div>}
            </div>
        );
    }

    return (
        <div style={{ height: '100%', overflowY: 'hidden', background: '#111', color: '#eee', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 20px', background: '#1a1a1a', borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    ðŸ ­ Factory V1
                    <Tooltip content="Last successful token validation time" side="bottom">
                        <span style={{ fontSize: '12px', background: '#1b5e20', padding: '2px 6px', borderRadius: '4px', cursor: 'default' }}>ðŸ”’ Authenticated</span>
                    </Tooltip>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Tooltip content="Go to Developer Console. Calls: Navigate /admin/console" side="bottom">
                        <button onClick={() => window.location.href = '/admin/console'} style={{ background: '#4a148c', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>Console</button>
                    </Tooltip>

                    <Tooltip content="Legacy Pipeline: CSV Batch Processing" side="bottom">
                        <button onClick={() => setActiveTab('pipeline')} style={{ background: activeTab === 'pipeline' ? '#2e7d32' : '#333', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>Pipeline</button>
                    </Tooltip>
                    <Tooltip content="Manifest Editor: Books, Source, Covers" side="bottom">
                        <button onClick={() => setActiveTab('manifest')} style={{ background: activeTab === 'manifest' ? '#2e7d32' : '#333', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>Manifest</button>
                    </Tooltip>
                    <Tooltip content="Queue Manager: Running, Waiting, Failed Jobs" side="bottom">
                        <button onClick={() => setActiveTab('queue')} style={{ background: activeTab === 'queue' ? '#2e7d32' : '#333', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>Queue</button>
                    </Tooltip>
                    <Tooltip content="Maintenance: Audits, Repairs, Cleanup" side="bottom">
                        <button onClick={() => setActiveTab('maintain')} style={{ background: activeTab === 'maintain' ? '#2e7d32' : '#333', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>Maintain</button>
                    </Tooltip>
                    <div style={{ marginLeft: 10, alignSelf: 'center', color: '#888', fontSize: 11, borderLeft: '1px solid #444', paddingLeft: 10 }}>
                        <Tooltip content="Hover or Long-Press any control for details" side="left">
                            <span style={{ cursor: 'help' }}>? Help</span>
                        </Tooltip>
                    </div>
                </div>
            </div>
            <div style={{ flex: 1, overflowY: 'hidden', padding: '10px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                {activeTab === 'pipeline' && <PipelineView />}
                {activeTab === 'manifest' && <ManifestEditor />}
                {activeTab === 'queue' && <QueuePanel />}
                {activeTab === 'maintain' && <MaintenanceView />}
            </div>
        </div>
    );
};

// ==========================================
// PIPELINE VIEW
// ==========================================
const PipelineView = () => {
    // Persistent State Loader
    const loadState = () => {
        try {
            const s = localStorage.getItem('factory_v1_state');
            return s ? JSON.parse(s) : {};
        } catch { return {}; }
    };
    const saved = loadState();

    const [csv, setCsv] = useState(saved.csv || 'id,sourceUrl,title,author,voice,mode,coverPrompt\n');
    const [currentIndex, setCurrentIndex] = useState(saved.currentIndex || 0);
    const [parsedRows, setParsedRows] = useState<any[]>([]);

    // UI State
    const [csvCollapsed, setCsvCollapsed] = useState(true);
    const [preview, setPreview] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('');
    const [lastError, setLastError] = useState('');

    // Trim State
    const [startOffset, setStartOffset] = useState(saved.startOffset || 0);
    const [endOffset, setEndOffset] = useState(saved.endOffset || 0);
    const [trimMode, setTrimMode] = useState<'start' | 'end'>('start');

    // Row Metadata State (reset on row change)
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');

    // Viewport
    const [textTab, setTextTab] = useState<'start' | 'end' | 'head' | 'tail'>('start');
    const previewRef = useRef<HTMLTextAreaElement>(null);

    // Cover State
    const [coverImg, setCoverImg] = useState<string | null>(null);
    const [coverLoading, setCoverLoading] = useState(false);
    const [coverStyleStyle, setCoverStyleState] = useState(0);
    const [coverPrompt, setCoverPrompt] = useState('');
    const [coverModel, setCoverModel] = useState('dall-e-3');
    const [bakeTitle, setBakeTitle] = useState(false);

    // Save State Effect
    useEffect(() => {
        const s = {
            csv, currentIndex, startOffset, endOffset,
            title, author, coverPrompt, coverModel
        };
        localStorage.setItem('factory_v1_state', JSON.stringify(s));
    }, [csv, currentIndex, startOffset, endOffset, title, author, coverPrompt, coverModel]);

    // Parse CSV
    useEffect(() => {
        const lines = csv.split('\n').map(l => l.trim()).filter(l => l);
        if (lines.length === 0) { setParsedRows([]); return; }

        // Detect delimiter (comma, tab, semicolon)
        const firstLine = lines[0];
        let delim = ',';
        if (firstLine.includes('\t')) delim = '\t';
        else if (firstLine.includes(';')) delim = ';';

        const headers = lines[0]?.split(delim).map(h => h.trim().replace(/^"|"$/g, '')) || [];

        // Header Mapping (Normalize)
        const normalizedHeaders = headers.map(h => {
            const low = h.toLowerCase();
            if (low === 'url' || low === 'link' || low === 'source') return 'sourceUrl';
            if (low === 'id' || low === 'slug') return 'id';
            return h;
        });

        const rows = lines.slice(1).map((l, i) => {
            const cols = l.split(delim).map(c => c.trim().replace(/^"|"$/g, ''));
            const obj: any = {};
            normalizedHeaders.forEach((h, idx) => obj[h] = cols[idx]);
            // Sanitize ID
            if (obj.id) obj.id = obj.id.replace(/\.webp$|\.png$|\.jpg$/i, '');
            return { _i: i, ...obj };
        });
        setParsedRows(rows);

        // AUTO-SELECT FIRST ROW IF NONE SELECTED (or if previous selection is invalid)
        // If we just pasted new CSV, we want to start at index 0 immediately.
        if (rows.length > 0) {
            setCurrentIndex(0); // Reset to 0 on new CSV paste
        }
    }, [csv]);

    const currentRow = parsedRows[currentIndex];
    const setStatusW = (s: string, isErr = false) => { setStatus(s); if (isErr) setLastError(s); };

    // P0-A: ROW CHANGE HANDLER (Reset State & Load)
    useEffect(() => {
        if (!currentRow) return;

        // Reset all row-specific state to clean slate
        setPreview(null);
        setCoverImg(null);
        setCoverPrompt('');
        setTitle('');
        setAuthor('');
        setStartOffset(0);
        setEndOffset(0);
        setLastError('');
        setStatusW('Row changed. Loading...');

        loadRow(currentRow);
    }, [currentIndex, currentRow?.sourceUrl]); // Depend on index + ID/Url




    // P0-D: Load Row & Auto-Cover
    const loadRow = async (row: any) => {
        if (!row || (!row.sourceUrl && !row.id)) { setStatusW("Invalid Row / End of Batch", true); return; }
        setLoading(true);

        try {
            const r = await adminFetch('/api/factory/preview', {
                method: 'POST',
                body: JSON.stringify({
                    sourceUrl: row.sourceUrl,
                    id: row.id,
                    title: row.title,
                    author: row.author
                })
            });
            const d = await r.json();
            if (d.ok) {
                setPreview(d);

                // Metadata Defaults
                const t = row.title || d.title || "";
                const a = row.author || d.author || "";
                setTitle(t);
                setAuthor(a);
                // We don't have year in preview yet, assume empty or 2024

                // Smart Start
                if (d.suggestions && d.suggestions.length > 0) {
                    setStartOffset(d.suggestions[0].offset);
                    setStatusW(`Loaded. Found: ${d.suggestions[0].label}`);
                } else {
                    setStartOffset(0);
                    setStatusW('Loaded.');
                }

                setEndOffset(d.lengthChars || 0);

                // Auto-Cover
                const userPrompt = row.coverPrompt || "";
                generateCover(t || "Book", a || "Unknown", 0, userPrompt);

            } else {
                setStatusW(`Preview Error: ${d.error}`, true);
            }
        } catch (e) { setStatusW(`Error: ${e}`, true); }
        setLoading(false);
    };

    const COVER_STYLES = [
        "classic vintage paperback cover, elegant typography, centered composition, high detail",
        "oil painting style, rich textures, golden age illustration, masterpiece",
        "minimalist geometric symbol, modernist book cover, clean lines, bauhaus",
        "gothic engraving style, detailed line work, dark atmosphere, woodcut",
        "abstract expressionist art, bold colors, artistic interpretation, vibrant"
    ];

    const generateCover = async (t: string, a: string, styleIdx: number, userPrompt?: string) => {
        if (!currentRow || !currentRow.id) { setStatusW("No Row/ID selected.", true); return; }
        setCoverLoading(true);

        const style = COVER_STYLES[styleIdx % COVER_STYLES.length];
        const variant = Math.floor(Math.random() * 1000);
        // Note: bakeTitle logic is applied AFTER generation now.
        const base = userPrompt || style;
        const prompt = `${base}. Title: "${t}", Author: ${a}. Flat 2D cover (NOT a 3D book mockup). Include the exact Title + Author exactly once total. No duplicate title anywhere; no subtitle; no tagline; no extra text. No UI overlay text. [v${variant}]`;
        setCoverPrompt(prompt);

        try {
            // New Server-Side Generation (gpt-image-1.5 -> R2)
            const r = await adminFetch(`/api/library/cover/${currentRow.id}/generate`, {
                method: 'POST', body: JSON.stringify({ prompt, title: t, author: a })
            });

            if (!r.ok) {
                const err = await formatError(r);
                setStatusW(`Gen Error: ${err}`, true);
            } else {
                const d = await r.json();
                const freshUrl = `${d.coverUrl}?ts=${Date.now()}`;

                // If Bake Title is ON, we must download, bake, and Re-Upload
                if (bakeTitle) {
                    setStatusW("Baking Title...");
                    const bakedData = await bakeText(d.coverUrl, t, a);
                    setCoverImg(bakedData); // Local preview as Data URL
                    // We don't upload executed immediately? 
                    // The prompt said "Buttons: Generate... Upload..."
                    // If I auto-upload the baked version, it's consistent.
                    // But let's leave it as "Preview matches Data URL", handleApprove uploads it.
                    // Wait, if I Regenerate, I expect the result to be saved.
                    // The Server Generation saved the RAW image.
                    // If I bake, I am modifying it locally. 
                    // I should probably auto-upload baked version if it was auto-generated?
                    // Existing flow: generate -> setCoverImg -> User Approves -> Uploads.
                    // New flow: generate -> (Server saves) -> setCoverImg(URL).
                    // If bake: ... -> setCoverImg(Data).
                    // If user approves: Uploads Data (Overwriting server raw). 
                    // This works.
                } else {
                    setCoverImg(freshUrl);
                    setStatusW('Cover Ready (Saved to R2).');
                }
            }
        } catch (e) { setStatusW(`Net Error: ${e}`, true); }
        setCoverLoading(false);
    };

    const bakeText = (dataUrl: string, title: string, author: string): Promise<string> => {
        return new Promise(resolve => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                const c = document.createElement('canvas');
                c.width = img.width; c.height = img.height;
                const ctx = c.getContext('2d');
                if (!ctx) return resolve(dataUrl);
                ctx.drawImage(img, 0, 0);

                // Overlay
                const grd = ctx.createLinearGradient(0, c.height - 400, 0, c.height);
                grd.addColorStop(0, "rgba(0,0,0,0)");
                grd.addColorStop(0.7, "rgba(0,0,0,0.8)");
                grd.addColorStop(1, "rgba(0,0,0,0.9)");
                ctx.fillStyle = grd;
                ctx.fillRect(0, c.height - 400, c.width, 400);

                ctx.textAlign = 'center';
                ctx.fillStyle = 'white';

                // Title
                ctx.font = 'bold 80px serif';
                const words = title.split(' ');
                let line = '';
                let y = c.height - 180;
                if (words.length > 5 || title.length > 30) {
                    ctx.font = 'bold 60px serif';
                    ctx.fillText(title.slice(0, 30) + "...", c.width / 2, y);
                } else {
                    ctx.fillText(title, c.width / 2, y);
                }

                // Author
                ctx.font = '40px sans-serif';
                ctx.fillText(author.toUpperCase(), c.width / 2, c.height - 80);

                resolve(c.toDataURL('image/png', 0.9));
            };
            img.src = dataUrl;
        });
    };

    const handleUpload = async (file: File) => {
        if (!currentRow) return;
        setCoverLoading(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            const b64 = e.target?.result as string;
            // Upload
            try {
                const r = await adminFetch(`/api/library/cover/${currentRow.id}`, {
                    method: 'POST',
                    body: JSON.stringify({
                        imageDataUrl: b64,
                        title: title || currentRow.title,
                        author: author || currentRow.author
                    })
                });
                if (r.ok) {
                    const d = await r.json();
                    setCoverImg(`${d.coverUrl}?ts=${Date.now()}`);
                    setStatusW("Manual Upload Success");
                } else {
                    setStatusW(`Upload Failed: ${await formatError(r)}`, true);
                }
            } catch (err) { setStatusW(`Upload Net Error: ${err}`, true); }
            setCoverLoading(false);
        };
        reader.readAsDataURL(file);
    };

    const handleApprove = async () => {
        if (!currentRow || !preview) return;
        setLoading(true);
        setStatusW("Approving...");

        try {
            let finalCoverUrl = "";
            // If we have a local modification (Bake) or Manual Upload waiting (if we did that way statefully),
            // But we handled manual upload immediately above.
            // So only if bake logic made it a Data URL do we need to upload again.
            if (coverImg && coverImg.startsWith('data:')) {
                setStatusW("Uploading baked cover...");
                const rC = await adminFetch(`/api/library/cover/${currentRow.id}`, {
                    method: 'POST',
                    body: JSON.stringify({
                        imageDataUrl: coverImg,
                        title: title || currentRow.title,
                        author: author || currentRow.author,
                        prompt: coverPrompt
                    })
                });

                if (!rC.ok) throw new Error(`Cover Upload Failed: ${await formatError(rC)}`);
                const dC = await rC.json();
                finalCoverUrl = dC.coverUrl;
            } else if (coverImg) {
                // Remove param timestamps if present
                finalCoverUrl = coverImg.split('?')[0];
            }

            setStatusW("Cooking...");
            const params = {
                sourceUrl: currentRow.sourceUrl,
                voice: currentRow.voice || 'onyx',
                mode: currentRow.mode || 'sample',
                approval: 'auto',
                startOffset,
                endOffset,
                id: currentRow.id,
                title: title || currentRow.title,
                author: author || currentRow.author,
                coverUrl: finalCoverUrl
            };
            const r = await adminFetch('/api/factory/cook', { method: 'POST', body: JSON.stringify(params) });

            if (r.ok) {
                const d = await r.json();
                setStatusW(`Job Queued: ${d.id}`);
                setTimeout(() => setCurrentIndex(i => i + 1), 1500);
            } else if (r.status === 409) {
                const d = await r.json();
                setDuplicateJob(d.existingJob);
                setPendingCookParams(params);
                setStatusW("Duplicate Job Detected");
            } else {
                setStatusW(`Cook Failed: ${await formatError(r)}`, true);
            }
        } catch (e) { setStatusW(`Net Error: ${e}`, true); }
        setLoading(false);
    };

    const handleDuplicateResolve = async (mode: 'replace' | 'reset') => {
        if (!pendingCookParams) return;

        // Confirmation for RESET
        if (mode === 'reset') {
            if (!confirm("Are you SURE? This will DELETE all existing audio and playlists for this book. The cover art will be preserved.")) return;
        }

        setDuplicateJob(null);
        setLoading(true);
        setStatusW(`Resolving duplicate (${mode})...`);

        try {
            const newParams = { ...pendingCookParams, resolution: mode };
            const r = await adminFetch('/api/factory/cook', { method: 'POST', body: JSON.stringify(newParams) });

            if (r.ok) {
                const d = await r.json();
                setStatusW(`Job Queued (${mode}): ${d.id}`);
                setTimeout(() => setCurrentIndex(i => i + 1), 1500);
            } else {
                setStatusW(`Resolution Failed: ${await formatError(r)}`, true);
            }
        } catch (e) { setStatusW(`Net Error: ${e}`, true); }
        setLoading(false);
        setPendingCookParams(null);
    };

    // P0-F: BATCH PROCESSING
    const handleProcessBatch = async () => {
        if (parsedRows.length === 0) { setStatusW("No rows parsed. Check CSV format.", true); return; }

        const validRows = parsedRows.filter(r => r.id && (r.sourceUrl || r.text));
        if (validRows.length === 0) {
            setStatusW(`Found ${parsedRows.length} rows but 0 valid. Missing 'id' or 'sourceUrl' columns?`, true);
            // Try to help debug
            const first = parsedRows[0];
            const keys = first ? Object.keys(first).join(', ') : 'none';
            alert(`Error: No valid rows found.\nExpected columns: id, sourceUrl (or url/link)\nFound columns: ${keys}`);
            return;
        }

        if (!confirm(`Queue ${validRows.length} jobs from CSV?`)) return;

        setLoading(true);
        setStatusW(`Queueing ${validRows.length} jobs...`);
        let successes = 0;
        const errors: string[] = [];

        for (const row of validRows) {
            try {
                // Validate Mode
                const mode = row.mode || 'sample'; // Default sample for safety

                const params = {
                    id: row.id,
                    sourceUrl: row.sourceUrl,
                    title: row.title || row.id,
                    author: row.author || "Unknown",
                    voice: row.voice || "onyx",
                    mode: mode,
                    approval: 'auto'
                };

                const r = await adminFetch('/api/factory/cook', {
                    method: 'POST',
                    body: JSON.stringify(params)
                });

                if (r.ok) {
                    successes++;
                    // Optional: Mark row as done?
                } else {
                    errors.push(`${row.id}: ${await formatError(r)}`);
                }
            } catch (e: any) {
                errors.push(`${row.id}: ${e.message}`);
            }
        }

        setStatusW(`Batch Done. Queued: ${successes}, Failed: ${errors.length}`);
        if (errors.length > 0) {
            setLastError(errors.join(' | '));
            alert(`Some jobs failed:\n${errors.join('\n')}`);
        }
        setLoading(false);
    };

    const MARKER_START_TXT = "\n\nâœ‚ï¸ ----- [START] ----- âœ‚ï¸\n\n";
    const MARKER_END_TXT = "\n\nâœ‚ï¸ ----- [END] ----- âœ‚ï¸\n\n";

    const getViewportState = () => {
        if (!preview) return null;
        const isTail = textTab === 'tail';
        const txt = isTail ? (preview.textTail || "") : (preview.textHead || "");
        const docLen = preview.lengthChars || 0;
        const viewportStart = isTail ? (docLen - (preview.textTail?.length || 0)) : 0;

        // Determine where markers fall relative to this text
        let displayTxt = txt;
        let startMarkerPos = -1;
        let endMarkerPos = -1;

        // Relative offsets in ORIGINAL text
        const relStart = startOffset - viewportStart;
        const relEnd = endOffset - viewportStart;

        // START MARKER insertion
        if (relStart >= 0 && relStart <= txt.length) {
            startMarkerPos = relStart;
            // Cap at 0? relStart is >= 0 check.
            displayTxt = displayTxt.slice(0, relStart) + MARKER_START_TXT + displayTxt.slice(relStart);
        }

        // END MARKER insertion
        if (relEnd >= 0 && relEnd <= txt.length) {
            let insertAt = relEnd;
            // If start marker was inserted BEFORE this point, shift insertion point
            if (startMarkerPos !== -1 && relEnd >= relStart) {
                insertAt += MARKER_START_TXT.length;
            }
            endMarkerPos = insertAt;
            displayTxt = displayTxt.slice(0, insertAt) + MARKER_END_TXT + displayTxt.slice(insertAt);
        }

        return { displayTxt, viewportStart, startMarkerPos, endMarkerPos };
    };

    const setOffsetFromSelection = () => {
        try {
            if (!previewRef.current || !preview) return;
            const el = previewRef.current;
            const selStart = el.selectionStart;

            const state = getViewportState();
            if (!state) return;
            const { viewportStart, startMarkerPos, endMarkerPos } = state;

            // Map visual selStart to true relative offset
            let trueRel = selStart;

            // Did we click AFTER (or inside) start marker?
            if (startMarkerPos !== -1 && selStart > startMarkerPos) {
                // If inside marker, this snaps to start of marker logic roughly
                trueRel -= MARKER_START_TXT.length;
            }

            // Did we click AFTER (or inside) end marker?
            if (endMarkerPos !== -1 && selStart > endMarkerPos) {
                trueRel -= MARKER_END_TXT.length;
            }

            const absOffset = viewportStart + trueRel;

            if (trimMode === 'start') {
                setStartOffset(absOffset);
                setStatusW(`Start set to ${absOffset}`);
            } else {
                setEndOffset(absOffset);
                setStatusW(`End set to ${absOffset}`);
            }
        } catch (e) {
            console.error(e);
            setStatusW(`Selection Error: ${e}`, true);
        }
    };

    const renderViewport = () => {
        const state = getViewportState();
        if (!state) return <textarea readOnly style={{ width: '100%', height: '50px', background: '#000', color: '#555', border: 'none' }} value="No Preview Loaded" />;

        return (
            <textarea
                ref={previewRef}
                readOnly
                style={{
                    width: '100%',
                    height: '520px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre',
                    background: '#080808',
                    color: '#ccc',
                    border: '1px solid #333',
                    resize: 'vertical',
                    padding: '10px'
                }}
                value={state.displayTxt}
            />
        );
    };

    const adjustOffset = (delta: number) => {
        if (trimMode === 'start') setStartOffset(s => Math.max(0, s + delta));
        else setEndOffset(s => Math.max(0, Math.min(preview?.lengthChars || 0, s + delta)));
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', height: '100%' }}>

            {/* Top Info Bar */}
            <div style={{ background: '#222', padding: '10px', borderRadius: '8px', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setCsvCollapsed(!csvCollapsed)}>
                    <div style={{ fontWeight: 'bold' }}>
                        Row {currentIndex + 1}: <span style={{ color: '#aaa', fontWeight: 'normal' }}>{currentRow?.title || 'Unknown'}</span>
                    </div>
                    <div>
                        {lastError && <span style={{ color: '#f88', marginRight: '10px' }}>âš ï¸ {lastError}</span>}
                        <button onClick={handleProcessBatch} disabled={loading} style={{ background: '#2e7d32', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>
                            Process Batch ({parsedRows.length})
                        </button>
                        <span>{csvCollapsed ? 'Expand Batch v' : '^'}</span>
                    </div>
                </div>
                {!csvCollapsed && <textarea value={csv} onChange={e => setCsv(e.target.value)} style={{ width: '100%', height: '100px', marginTop: '10px', fontFamily: 'monospace', fontSize: '11px' }} />}
            </div>

            {/* Main Work Area */}
            <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: 0 }}>

                {/* Left: Text Trimmer */}
                <div style={{ flex: 2, background: '#1a1a1a', borderRadius: '8px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {/* Toolbar */}
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <Tooltip content="Show HEAD of text document" side="bottom">
                            <button onClick={() => setTextTab('head')} style={{ background: textTab === 'head' ? '#666' : '#444', color: 'white', padding: '4px 8px' }}>Head Portal</button>
                        </Tooltip>
                        <Tooltip content="Show TAIL of text document" side="bottom">
                            <button onClick={() => setTextTab('tail')} style={{ background: textTab === 'tail' ? '#666' : '#444', color: 'white', padding: '4px 8px' }}>Tail Portal</button>
                        </Tooltip>
                    </div>

                    <div style={{ width: '1px', height: '20px', background: '#555', margin: '0 5px' }}></div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#222', padding: '4px', borderRadius: '4px' }}>
                        <span style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase' }}>{trimMode}:</span>
                        <Tooltip content="Exact character offset" side="bottom">
                            <input
                                type="number"
                                value={trimMode === 'start' ? startOffset : endOffset}
                                onChange={e => trimMode === 'start' ? setStartOffset(parseInt(e.target.value)) : setEndOffset(parseInt(e.target.value))}
                                style={{ width: '80px', background: 'black', color: 'white', border: 'none', textAlign: 'right' }}
                            />
                        </Tooltip>
                        <Tooltip content="Shift back 50 chars" side="bottom">
                            <button onClick={() => adjustOffset(-50)}>&lt;</button>
                        </Tooltip>
                        <Tooltip content="Shift forward 50 chars" side="bottom">
                            <button onClick={() => adjustOffset(+50)}>&gt;</button>
                        </Tooltip>
                    </div>

                    <div style={{ display: 'flex', gap: '5px' }}>
                        <Tooltip content="Editing START offset mode" side="bottom">
                            <button onClick={() => setTrimMode('start')} style={{ background: trimMode === 'start' ? '#d32f2f' : '#444', color: 'white' }}>Edit START</button>
                        </Tooltip>
                        <Tooltip content="Editing END offset mode" side="bottom">
                            <button onClick={() => setTrimMode('end')} style={{ background: trimMode === 'end' ? '#d32f2f' : '#444', color: 'white' }}>Edit END</button>
                        </Tooltip>
                    </div>

                    <div style={{ flex: 1 }}></div>
                    <Tooltip content="Set currently selected offset from text selection" side="left">
                        <button onClick={setOffsetFromSelection} style={{ background: '#0277bd', color: 'white', fontSize: '11px', padding: '6px 12px' }}>SET FROM SELECTION</button>
                    </Tooltip>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', background: '#000', fontFamily: 'monospace', fontSize: '13px', padding: '20px', color: '#ccc' }}>
                    {renderViewport()}
                </div>
            </div>

            {/* Right: Metadata & Cover */}
            <div style={{ flex: 1, background: '#1a1a1a', borderRadius: '8px', padding: '15px', display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', minHeight: 0 }}>

                <div style={{ minHeight: '200px', background: '#000', borderRadius: '4px', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                    {coverLoading ? <span>Generating Art...</span> : (
                        coverImg ? <img src={coverImg} style={{ maxHeight: '300px', width: 'auto', maxWidth: '100%' }} /> : <span style={{ color: '#444' }}>No Cover</span>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '5px' }}>
                    <Tooltip content="Generate new cover with GPT-1.5. Calls: POST /api/library/cover/.../generate" side="top">
                        <button onClick={() => { setCoverStyleState(s => s + 1); generateCover(title, author, coverStyleStyle + 1, coverPrompt); }} style={{ flex: 1, padding: '6px' }}>
                            Regenerate (GPT-1.5)
                        </button>
                    </Tooltip>
                    <Tooltip content="Upload local image file" side="top">
                        <label style={{ cursor: 'pointer', background: '#444', color: 'white', padding: '6px 12px', borderRadius: '2px', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                            Upload
                            <input type="file" accept="image/*" onChange={(e) => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); }} style={{ display: 'none' }} />
                        </label>
                    </Tooltip>
                </div>

                <label style={{ fontSize: '11px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <input type="checkbox" checked={bakeTitle} onChange={e => setBakeTitle(e.target.checked)} />
                    Bake Title (Post-Process Overlay)
                </label>

                <label style={{ fontSize: '11px', color: '#888' }}>Prompt:</label>
                <textarea value={coverPrompt} onChange={e => setCoverPrompt(e.target.value)} style={{ height: '60px', fontSize: '10px', fontFamily: 'monospace' }} />

                <div style={{ borderTop: '1px solid #333', marginTop: '10px', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '11px', color: '#888' }}>Metadata</label>
                    <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" style={{ padding: '8px', background: '#222', color: 'white', border: '1px solid #444' }} />
                    <input value={author} onChange={e => setAuthor(e.target.value)} placeholder="Author" style={{ padding: '8px', background: '#222', color: 'white', border: '1px solid #444' }} />
                </div>

                <div style={{ flex: 1 }}></div>

                <Tooltip content="Submit job to queue. Calls: POST /api/factory/cook" side="top">
                    <button onClick={handleApprove} disabled={loading} style={{ padding: '15px', background: '#2e7d32', color: 'white', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' }}>
                        âœ… APPROVE & COOK
                    </button>
                </Tooltip>
                <Tooltip content="Skip this row without processing" side="top">
                    <button onClick={() => { setCurrentIndex(i => i + 1); setStatusW("Skipped."); }} style={{ padding: '10px', background: '#444', border: 'none', color: '#ccc' }}>
                        â ­ SKIP
                    </button>
                </Tooltip>
            </div>


            {/* Queue Mini View */}
            <div style={{ flexShrink: 0 }}>
                <QueuePanel mini />
            </div>
        </div >
    );
};

const ManifestEditor = () => {
    const [items, setItems] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [debugInfo, setDebugInfo] = useState<any>(null);
    const [editorText, setEditorText] = useState<string>('');
    const [editorMode, setEditorMode] = useState<'clean' | 'raw' | null>(null);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState('');
    const editorRef = useRef<HTMLTextAreaElement>(null);

    // New UI State
    const [activeActionId, setActiveActionId] = useState<string>('editor');
    const [showArtifacts, setShowArtifacts] = useState(false);
    const [artifactFilter, setArtifactFilter] = useState('');
    const [lastResult, setLastResult] = useState<{ ok: boolean, text: string } | null>(null);

    const refreshList = () => {
        adminFetch(`/api/factory/manifest?ts=${Date.now()}`)
            .then(r => r.json())
            .then(d => {
                if (d.ok) setItems(d.items || []);
                else setMsg(`List Error: ${d.error}`);
            })
            .catch(e => setMsg(`List Error: ${e}`));
    };

    useEffect(() => { refreshList(); }, []);

    const getSelectedItem = () => items.find(i => i.id === selectedId);

    const loadBook = async (id: string, silent = false) => {
        if (!silent) setLoading(true);
        setSelectedId(id);
        if (!silent) {
            setEditorMode(null);
            setMsg(`Loading ${id}...`);
            setActiveActionId('editor'); // Default to editor/verify
            setLastResult(null);
        }
        try {
            const r = await adminFetch(`/api/library/debug/keys/${id}`);
            const d = await r.json();
            if (d.ok) setDebugInfo(d.keys);
            else if (!silent) setMsg(`Debug Error: ${d.error}`);
        } catch (e) { if (!silent) setMsg(`Net Error: ${e}`); }
        if (!silent) setLoading(false);
    };

    const loadSource = async (variant: 'clean' | 'raw') => {
        if (!selectedId) return;
        setLoading(true);
        try {
            const r = await adminFetch(`/api/library/source/${selectedId}`);
            if (!r.ok) throw new Error(await formatError(r));
            const txt = await r.text();
            const v = r.headers.get("X-Source-Variant") as any;
            setEditorText(txt);
            setEditorMode(v || 'clean');
            setMsg(`Loaded ${v} source (${txt.length} chars)`);
        } catch (e) { setMsg(`Load Error: ${e}`); }
        setLoading(false);
    };

    const saveSource = async (targetVariant: 'clean' | 'raw') => {
        if (!selectedId) return;
        setLoading(true);
        try {
            const r = await adminFetch(`/api/library/source/${selectedId}?variant=${targetVariant}`, {
                method: 'PUT',
                body: editorText
            });
            if (!r.ok) throw new Error(await formatError(r));
            setMsg(`Saved to ${targetVariant} successfully.`);
            setLastResult({ ok: true, text: `Saved to ${targetVariant}` });
            loadBook(selectedId, true);
        } catch (e: any) {
            setMsg(`Save Error: ${e}`);
            setLastResult({ ok: false, text: e.message });
        }
        setLoading(false);
    };

    const deriveClean = async () => {
        if (!selectedId) return;
        setLoading(true);
        try {
            const r = await adminFetch(`/api/library/source/${selectedId}/derive-clean`, { method: 'POST' });
            if (!r.ok) throw new Error(await formatError(r));
            const d = await r.json();
            setMsg(`Derive Success: ${d.originalSize} -> ${d.cleanedSize} chars`);
            setLastResult({ ok: true, text: `Derived Clean Text: ${d.cleanedSize} chars` });
            loadBook(selectedId, true);
        } catch (e: any) {
            setMsg(`Derive Error: ${e}`);
            setLastResult({ ok: false, text: e.message || String(e) });
        }
        setLoading(false);
    };

    const handleSetStartFromSelection = async () => {
        if (!selectedId || !editorRef.current) return;
        const start = editorRef.current.selectionStart;
        const end = editorRef.current.selectionEnd;
        const selectedText = editorRef.current.value.slice(start, end).trim();

        if (!selectedText) { setMsg("Select text to use as marker first."); return; }
        const marker = selectedText.slice(0, 100);

        setLoading(true);
        try {
            const r = await adminFetch(`/api/library/manifest/${selectedId}/update`, {
                method: 'POST',
                body: JSON.stringify({ readingStartMode: 'marker', readingStartMarker: marker })
            });
            if (!r.ok) throw new Error(await formatError(r));
            setMsg(`Start Marker Set: "${marker}"`);
            setLastResult({ ok: true, text: `Start Marker Set` });
            refreshList();
        } catch (e: any) {
            setMsg(`Error setting start: ${e}`);
            setLastResult({ ok: false, text: e.message });
        }
        setLoading(false);
    };

    const handleTestPreview = async () => {
        if (!selectedId) return;
        setLoading(true);
        try {
            const r = await adminFetch(`/api/library/source/${selectedId}/preview?chars=600`);
            const d = await r.json();
            if (d.ok) {
                alert(`Effectively Starts At: ${d.startVal} (Method: ${d.method})\n\nPREVIEW:\n----------------\n${d.preview}`);
                setMsg(`Preview OK. Start: ${d.startVal}`);
                setLastResult({ ok: true, text: `Preview OK: Starts at ${d.startVal}` });
            } else {
                alert(`Preview Error: ${d.error}`);
                setLastResult({ ok: false, text: d.error });
            }
        } catch (e: any) {
            setMsg(`Preview Error: ${e}`);
            setLastResult({ ok: false, text: String(e) });
        }
        setLoading(false);
    };

    const handleRebuildPlan = async () => {
        if (!selectedId) return;
        if (!confirm("Confirm Rebuild Plan?\n\nThis will re-calculate audio chunks based on current text and settings. Existing audio files will NOT be deleted, but the playlist map will change.")) return;
        setLoading(true);
        try {
            const r = await adminFetch(`/api/factory/${selectedId}/rebuild_plan`, { method: 'POST' });
            const d = await r.json();
            if (d.ok) {
                setMsg(`Plan Rebuild: ${d.message}`);
                setLastResult({ ok: true, text: d.message });
            } else {
                setMsg(`Rebuild Error: ${d.error}`);
                setLastResult({ ok: false, text: d.error });
            }
        } catch (e: any) { setMsg(`Net Error: ${e}`); setLastResult({ ok: false, text: String(e) }); }
        setLoading(false);
    };

    const handleFullReset = async () => {
        if (!selectedId) return;
        const conf = prompt(`DANGER: FULL RESET\n\nType "${selectedId}" to DELETE ALL AUDIO and reset job state.`);
        if (conf !== selectedId && conf !== "DELETE") return;

        setLoading(true);
        try {
            const r = await adminFetch(`/api/factory/${selectedId}/reset_audio_and_plan`, {
                method: 'POST',
                body: JSON.stringify({ confirmation: conf })
            });
            const d = await r.json();
            if (d.ok) {
                setMsg(`RESET DONE. Deleted ${d.deleted ? d.deleted.r2 : 'unknown'} items.`);
                setLastResult({ ok: true, text: `RESET DONE. New Job: ${d.newJobId}` });
            } else {
                setMsg(`Reset Error: ${d.error}`);
                setLastResult({ ok: false, text: d.error });
            }
        } catch (e: any) { setMsg(`Net Error: ${e}`); setLastResult({ ok: false, text: String(e) }); }
        setLoading(false);
    };

    const handleNukeAndPave = async () => {
        if (!selectedId) return;
        const conf = prompt(`DANGER: NUKE & PAVE\n\nType "NUKE" to delete SOURCE, AUDIO, TEXT and reset EVERYTHING for ${selectedId}.`);
        if (conf !== "NUKE") return;

        setLoading(true);
        try {
            const r = await adminFetch(`/api/factory/${selectedId}/nuke_and_pave`, { method: 'POST' });
            const d = await r.json();
            if (d.ok) {
                setMsg(`NUKE DONE. Deleted ${d.deleted ? d.deleted.r2 : 'unknown'} items.`);
                setLastResult({ ok: true, text: d.message || "Nuke Complete" });
                refreshList();
            } else {
                setMsg(`Nuke Error: ${d.error}`);
                setLastResult({ ok: false, text: d.error });
            }
        } catch (e: any) { setMsg(`Net Error: ${e}`); setLastResult({ ok: false, text: String(e) }); }
        setLoading(false);
    };

    const handleRebuildThumbs = async (id: string, migrate = false) => {
        const verb = migrate ? "Migrate/Rebuild" : "Rebuild";
        if (!confirm(`${verb} thumbnails for ${id}?\n\nSafe operation. Will generate grid/full/hi variants from Master.`)) return;
        setLoading(true);
        try {
            const url = migrate ? `/api/library/cover/${id}/migrate` : `/api/library/cover/${id}/rebuild`;
            const r = await adminFetch(url, { method: 'POST' });
            const d = await r.json();
            if (r.ok) {
                setMsg(`${verb} OK. ${d.details || ''}`);
                setLastResult({ ok: true, text: `${verb} Success` });
                if (selectedId === id) loadBook(id, true);
            } else {
                setMsg(`${verb} Failed: ${d.error || await formatError(r)}`);
                setLastResult({ ok: false, text: d.error });
            }
        } catch (e: any) { setMsg(`Net Error: ${e}`); setLastResult({ ok: false, text: String(e) }); }
        setLoading(false);
    };

    const handleCoverUpload = async (file: File) => {
        if (!selectedId) return;
        setLoading(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const b64 = e.target?.result as string;
                const r = await adminFetch(`/api/library/cover/${selectedId}/upload`, {
                    method: 'POST',
                    body: JSON.stringify({ imageDataUrl: b64, title: getSelectedItem()?.title, author: getSelectedItem()?.author })
                });
                if (r.ok) {
                    setMsg("Cover Uploaded & Canonicalized");
                    setLastResult({ ok: true, text: 'Cover Uploaded' });
                    loadBook(selectedId, true);
                    refreshList();
                } else setMsg(`Upload Error: ${await formatError(r)}`);
            } catch (err: any) { setMsg(`Upload Net Error: ${err}`); setLastResult({ ok: false, text: String(err) }); }
            setLoading(false);
        };
        reader.readAsDataURL(file);
    };

    // --- Action Definitions ---
    const ACTIONS = [
        { id: 'editor', label: 'Check Text & Markers', cat: 'cleanup', desc: 'Verify source/clean text, set start markers, and edit manually.', safe: true },
        { id: 'derive', label: 'Derive Clean Text', cat: 'cleanup', desc: 'Auto-generate clean text from source. Fixes mojibake/formatting.', safe: true, run: deriveClean },
        { id: 'thumbs', label: 'Rebuild Thumbnails', cat: 'assets', desc: 'Generate web-optimized covers (grid/full) from Master.', safe: true, run: () => handleRebuildThumbs(selectedId!) },
        { id: 'cover_mig', label: 'Migrate Legacy Cover', cat: 'assets', desc: 'Attempt to recover cover from old paths if missing.', safe: true, run: () => handleRebuildThumbs(selectedId!, true) },
        { id: 'plan', label: 'Rebuild Plan', cat: 'advanced', desc: 'Recalculate audio chunks. Use if text changed but audio is fine.', safe: false, run: handleRebuildPlan },
        { id: 'reset', label: 'Force Rebuild Audio', cat: 'destructive', desc: 'DELETE ALL AUDIO, reset job, and re-queue. Keeps source text.', safe: false, run: handleFullReset },
        { id: 'nuke', label: 'Nuke & Pave (Full)', cat: 'destructive', desc: 'DELETE EVERYTHING (Source + Audio) and start fresh.', safe: false, run: handleNukeAndPave },
    ];

    const getRecommendation = () => {
        if (!debugInfo) return null;
        const p = (k: string) => debugInfo[k]?.exists;
        const sid = selectedId;
        if (!p(`library/${sid}/source/cleaned.txt`)) return 'derive';
        if (p(`library/${sid}/cover/cover_master.png`) && !p(`library/${sid}/cover_grid.webp`)) return 'thumbs';
        return null;
    };
    const recommendedId = getRecommendation();

    // --- Renders ---
    const renderChecklist = () => {
        if (!selectedId || !debugInfo) return null;
        const sid = selectedId;
        const p = (k: string) => debugInfo[k]?.exists;
        const Check = ({ label, ok, title }: any) => (
            <div title={title} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', borderBottom: '1px solid #333' }}>
                <span style={{ color: '#aaa' }}>{label}</span>
                <span style={{ fontWeight: 'bold', color: ok ? '#4caf50' : '#d32f2f' }}>{ok ? 'PRESENT' : 'MISSING'}</span>
            </div>
        );
        return (
            <div style={{ background: '#333', borderRadius: '4px', padding: '10px', marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', marginBottom: '5px', textTransform: 'uppercase' }}>Artifact Verification</div>
                <Check label="Source Text" ok={p(`library/${sid}/source/text.txt`)} title="Original upload" />
                <Check label="Cleaned Text" ok={p(`library/${sid}/source/cleaned.txt`)} title="Normalized text for TTS" />
                <Check label="Cover Master" ok={p(`library/${sid}/cover/cover_master.png`)} title="High-res master image" />
                <Check label="Thumbnails" ok={p(`library/${sid}/cover_grid.webp`) && p(`library/${sid}/cover_full.webp`)} title="Web-optimized images" />
                <Check label="Manifest" ok={true} title="Metadata Record" />
                <Check label="Playlist" ok={p(`library/${sid}/playlist.json`)} title="Audio mapping" />
            </div>
        );
    };

    if (selectedId) {
        const item = getSelectedItem();
        const activeAct = ACTIONS.find(a => a.id === activeActionId) || ACTIONS[0];

        // Group Actions
        const groups = {
            'Recommended': ACTIONS.filter(a => a.id === recommendedId),
            'Cleanup': ACTIONS.filter(a => a.cat === 'cleanup' && a.id !== recommendedId),
            'Assets': ACTIONS.filter(a => a.cat === 'assets' && a.id !== recommendedId),
            'Advanced': ACTIONS.filter(a => (a.cat === 'advanced' || a.cat === 'destructive') && a.id !== recommendedId),
        };

        return (
            <div style={{ display: 'flex', height: '100%', background: '#222', overflow: 'hidden' }}>
                {/* LEFT SIDEBAR */}
                <div style={{ width: '320px', display: 'flex', flexDirection: 'column', borderRight: '1px solid #000', background: '#1e1e1e', flexShrink: 0 }}>
                    {/* Header */}
                    <div style={{ padding: '10px', borderBottom: '1px solid #333', background: '#252525' }}>
                        <button onClick={() => setSelectedId(null)} style={{ background: 'transparent', color: '#aaa', border: 'none', cursor: 'pointer', marginBottom: '5px' }}>â†  Back to List</button>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item?.title}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{selectedId}</div>
                    </div>

                    {/* Scrollable Content */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
                        {renderChecklist()}

                        {Object.entries(groups).map(([name, acts]) => (
                            acts.length > 0 && (
                                <div key={name} style={{ marginBottom: '15px' }}>
                                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', marginBottom: '5px' }}>{name}</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        {acts.map(a => (
                                            <Tooltip key={a.id} content={a.desc} side="right">
                                                <button
                                                    onClick={() => setActiveActionId(a.id)}
                                                    style={{
                                                        textAlign: 'left', padding: '10px',
                                                        background: activeActionId === a.id ? '#0277bd' : name === 'Recommended' ? '#e65100' : '#333',
                                                        color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer',
                                                        borderLeft: a.cat === 'destructive' ? '3px solid #d32f2f' : '3px solid transparent'
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{a.label}</div>
                                                    <div style={{ fontSize: '10px', opacity: 0.7 }}>{a.desc}</div>
                                                </button>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>
                            )
                        ))}
                    </div>

                    {/* Artifacts Toggle (Bottom) */}
                    {/* Artifacts Toggle (Bottom) */}
                    <div style={{ borderTop: '1px solid #333', display: 'flex', flexDirection: 'column', flex: showArtifacts ? 1 : 0, minHeight: showArtifacts ? '300px' : 'auto', transition: 'all 0.2s' }}>
                        <button
                            onClick={() => setShowArtifacts(!showArtifacts)}
                            style={{ padding: '10px', background: '#252525', color: '#ccc', border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}
                        >
                            <span>📦 All Artifacts ({debugInfo ? Object.keys(debugInfo).length : 0})</span>
                            <span>{showArtifacts ? '▼' : '▲'}</span>
                        </button>

                        {showArtifacts && (
                            <ArtifactsList
                                debugInfo={debugInfo}
                                id={selectedId}
                                filter={artifactFilter}
                                onFilterChange={setArtifactFilter}
                            />
                        )}
                    </div>
                </div>

                {/* MAIN CONTENT Area */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#121212', position: 'relative' }}>

                    {/* Active Action Header */}
                    <div style={{ padding: '15px', background: '#1a1a1a', borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '18px' }}>{activeAct.label}</h2>
                            <div style={{ color: '#888', fontSize: '13px', marginTop: '5px' }}>{activeAct.desc}</div>
                        </div>
                        {activeAct.run && (
                            <Tooltip content={`Executes ${activeAct.label}. ${activeAct.safe ? 'Safe operation.' : 'SIDE EFFECTS.'}`} side="left">
                                <button
                                    onClick={activeAct.run}
                                    disabled={loading}
                                    style={{
                                        padding: '10px 20px', fontWeight: 'bold', borderRadius: '4px', border: 'none', cursor: 'pointer',
                                        background: activeAct.cat === 'destructive' ? '#b71c1c' : '#2e7d32', color: 'white'
                                    }}
                                >
                                    {loading ? 'Running...' : `â–¶ Run ${activeAct.label}`}
                                </button>
                            </Tooltip>
                        )}
                    </div>

                    {/* Editor / Content */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '0', display: 'flex', flexDirection: 'column' }}>
                        {activeAct.id === 'editor' && (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <div style={{ background: '#333', padding: '5px 10px', display: 'flex', gap: '10px' }}>
                                    <Tooltip content="Show Cleaned Text (Normalized)" side="bottom">
                                        <button onClick={() => loadSource('clean')} style={{ fontSize: '11px', background: editorMode === 'clean' ? '#555' : 'transparent', color: editorMode === 'clean' ? 'white' : '#aaa', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>CLEAN</button>
                                    </Tooltip>
                                    <Tooltip content="Show Raw Source Text" side="bottom">
                                        <button onClick={() => loadSource('raw')} style={{ fontSize: '11px', background: editorMode === 'raw' ? '#555' : 'transparent', color: editorMode === 'raw' ? 'white' : '#aaa', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>RAW</button>
                                    </Tooltip>
                                    <div style={{ flex: 1 }}></div>
                                    <Tooltip content="Test effective start logic with current markers" side="bottom">
                                        <button onClick={handleTestPreview} style={{ fontSize: '11px', background: '#0277bd', color: 'white', border: 'none', borderRadius: '2px', cursor: 'pointer', padding: '4px 8px' }}>Preview Start</button>
                                    </Tooltip>
                                    <Tooltip content="Save text changes to R2. Calls: POST /api/library/source/:id" side="left">
                                        <button onClick={() => saveSource(editorMode || 'clean')} disabled={loading || !editorMode} style={{ fontSize: '11px', background: '#388e3c', color: 'white', border: 'none', borderRadius: '2px', cursor: 'pointer', padding: '4px 8px' }}>SAVE Changes</button>
                                    </Tooltip>
                                </div>
                                {editorMode ? (
                                    <textarea
                                        ref={editorRef}
                                        value={editorText}
                                        onChange={e => setEditorText(e.target.value)}
                                        style={{ flex: 1, background: '#111', color: '#ccc', fontFamily: 'monospace', padding: '20px', fontSize: '14px', border: 'none', resize: 'none', lineHeight: '1.5' }}
                                        spellCheck={false}
                                    />
                                ) : (
                                    <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                                        <button onClick={() => loadSource('clean')} style={{ padding: '10px 20px', background: '#333', color: 'white', border: 'none', cursor: 'pointer' }}>Load Text Editor</button>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeAct.id === 'thumbs' && (
                            <div style={{ padding: '20px', textAlign: 'center' }}>
                                <img src={`/api/library/cover/${selectedId}?ts=${Date.now()}`} style={{ maxHeight: '400px', maxWidth: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }} />
                                <div style={{ marginTop: '20px', color: '#aaa' }}>Current Master Cover</div>
                            </div>
                        )}

                        {/* Result Log */}
                        {lastResult && (
                            <div style={{ background: lastResult.ok ? '#1b5e20' : '#b71c1c', padding: '10px', color: 'white', fontFamily: 'monospace', fontSize: '12px' }}>
                                Result: {lastResult.text}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Default List View
    return (
        <div style={{ background: '#222', padding: '20px', overflowY: 'auto', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ margin: 0 }}>Library Manager ({items.length})</h3>
                <button onClick={refreshList} style={{ background: '#444', color: '#fff', padding: '5px 10px', border: 'none' }}>Refresh</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                    <tr style={{ background: '#333', textAlign: 'left' }}>
                        <th style={{ padding: '8px' }}>Cover</th>
                        <th style={{ padding: '8px' }}>Title / ID</th>
                        <th style={{ padding: '8px' }}>Summary</th>
                        <th style={{ padding: '8px' }}>Updated</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(book => (
                        <tr key={book.id}
                            style={{ background: 'transparent', cursor: 'pointer', borderBottom: '1px solid #333' }}
                            onClick={() => loadBook(book.id)}
                            onMouseOver={e => e.currentTarget.style.background = '#333'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <td style={{ padding: '8px', width: '50px' }}>
                                {book.coverUrl ? <img src={book.coverUrl} style={{ width: '40px', height: '60px', objectFit: 'cover' }} loading="lazy" /> : <div style={{ width: '40px', height: '60px', background: '#444' }}></div>}
                            </td>
                            <td style={{ padding: '8px' }}>
                                <div style={{ fontWeight: 'bold' }}>{book.title}</div>
                                <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '3px' }}>{book.author}</div>
                                <div style={{ fontSize: '10px', fontFamily: 'monospace', color: '#666' }}>{book.id}</div>
                            </td>
                            <td style={{ padding: '8px' }}>
                                {book.audioUrl && <span title="Narrated" style={{ marginRight: '5px', fontSize: '16px' }}>🎧</span>}
                            </td>
                            <td style={{ padding: '8px', color: '#aaa', fontSize: '11px' }}>
                                {book.updatedAt ? new Date(book.updatedAt).toLocaleDateString() : '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ==========================================
// ARTIFACTS LIST HELPER
// ==========================================
const ArtifactsList = ({ debugInfo, items, id, filter, onFilterChange }: any) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewTitle, setPreviewTitle] = useState('');

    // Sorting & Grouping
    const groups = useMemo(() => {
        const g: Record<string, any[]> = { Audio: [], JSON: [], Images: [], Other: [] };

        // Normalize input to list
        let list: any[] = [];
        if (items) {
            list = items.map((x: any) => ({
                key: x.key,
                filename: x.key.split('/').pop(),
                info: { size: x.size, exists: true },
                url: x.url || `/api/library/artifact/${id}/${x.key.split('/').pop()}`
            }));
        } else if (debugInfo) {
            list = Object.entries(debugInfo)
                .filter(([, info]: any) => info.exists) // Only show existing
                .map(([key, info]: any) => ({
                    key,
                    filename: key.split('/').pop(),
                    info,
                    url: `/api/library/artifact/${id}/${key.split('/').pop()}`
                }));
        }

        list.forEach(item => {
            if (filter && !item.filename.toLowerCase().includes(filter.toLowerCase())) return;

            if (item.filename.endsWith('.mp3') || item.filename.endsWith('.m4a')) {
                const match = item.filename.match(/(\d+)/);
                item.num = match ? parseInt(match[0]) : -1;
                g.Audio.push(item);
            } else if (item.filename.endsWith('.json')) {
                g.JSON.push(item);
            } else if (item.filename.match(/\.(webp|png|jpg|jpeg)$/)) {
                g.Images.push(item);
            } else {
                g.Other.push(item);
            }
        });

        // Sort Audio: DESCENDING by number
        g.Audio.sort((a, b) => b.num - a.num);

        // Sort others by name
        g.JSON.sort((a, b) => a.filename.localeCompare(b.filename));
        g.Images.sort((a, b) => a.filename.localeCompare(b.filename));

        return g;
    }, [debugInfo, items, filter, id]);

    const playAudio = (url: string, title: string) => {
        setPreviewUrl(url);
        setPreviewTitle(title);
    };

    const playLatest = () => {
        if (groups.Audio.length > 0) {
            const latest = groups.Audio[0]; // Sort is DESC
            playAudio(latest.url, `Latest: ${latest.filename}`);
        }
    };

    const getDownloadUrl = (filename: string) => `/api/library/artifact/${id}/${filename}?download=true`;
    const getViewUrl = (filename: string) => `/api/library/artifact/${id}/${filename}`;

    return (
        <div style={{ flex: 1, overflowY: 'auto', background: '#111', padding: '0', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '8px', background: '#1e1e1e', borderBottom: '1px solid #333' }}>
                <input
                    placeholder="Filter artifacts..."
                    value={filter}
                    onChange={e => onFilterChange(e.target.value)}
                    style={{ width: '100%', padding: '6px', background: '#333', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px' }}
                />
            </div>

            {groups.Audio.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ padding: '8px 10px', background: '#252525', fontSize: '11px', fontWeight: 'bold', color: '#aaa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>AUDIO ({groups.Audio.length})</span>
                        <button
                            onClick={playLatest}
                            style={{ background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            ▶ Play Latest ({groups.Audio[0].filename})
                        </button>
                    </div>
                    {groups.Audio.map((item: any) => (
                        <div key={item.key} style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #222', fontSize: '12px' }}>
                            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ color: '#81c784' }}>🎵</span>
                                <span title={item.filename}>{item.filename}</span>
                                <span style={{ color: '#555', fontSize: '10px' }}>{(item.info.size / 1024).toFixed(0)}kb</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={() => playAudio(item.url, item.filename)} title="Play" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>▶️</button>
                                <a href={getDownloadUrl(item.filename)} download title="Download" style={{ textDecoration: 'none', fontSize: '14px' }}>⬇️</a>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {groups.JSON.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ padding: '6px 10px', background: '#252525', fontSize: '11px', fontWeight: 'bold', color: '#aaa' }}>JSON / DATA</div>
                    {groups.JSON.map((item: any) => (
                        <div key={item.key} style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #222', fontSize: '12px' }}>
                            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ color: '#ffcc80' }}>{ }</span>
                                <span title={item.filename}>{item.filename}</span>
                                <span style={{ color: '#555', fontSize: '10px' }}>{(item.info.size / 1024).toFixed(0)}kb</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <a href={getViewUrl(item.filename)} target="_blank" rel="noopener noreferrer" title="View" style={{ textDecoration: 'none', fontSize: '14px' }}>📄</a>
                                <a href={getDownloadUrl(item.filename)} download title="Download" style={{ textDecoration: 'none', fontSize: '14px' }}>⬇️</a>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {groups.Images.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ padding: '6px 10px', background: '#252525', fontSize: '11px', fontWeight: 'bold', color: '#aaa' }}>IMAGES</div>
                    {groups.Images.map((item: any) => (
                        <div key={item.key} style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #222', fontSize: '12px' }}>
                            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ color: '#ba68c8' }}>🖼️</span>
                                <span title={item.filename}>{item.filename}</span>
                                <span style={{ color: '#555', fontSize: '10px' }}>{(item.info.size / 1024).toFixed(0)}kb</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <a href={getViewUrl(item.filename)} target="_blank" rel="noopener noreferrer" title="View" style={{ textDecoration: 'none', fontSize: '14px' }}>👁️</a>
                                <a href={getDownloadUrl(item.filename)} download title="Download" style={{ textDecoration: 'none', fontSize: '14px' }}>⬇️</a>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {previewUrl && (
                <AudioPlayerOverlay
                    url={previewUrl}
                    title={previewTitle}
                    onClose={() => setPreviewUrl(null)}
                />
            )}
        </div>
    );
};

// ==========================================
// QUEUE PANEL (OPERATOR CONSOLE)
// ==========================================
const QueuePanel = ({ mini }: { mini?: boolean }) => {
    const [queue, setQueue] = useState<any>(null);
    const [heartbeat, setHeartbeat] = useState<any>(null);
    const [counts, setCounts] = useState<any>({});
    const [deployTag, setDeployTag] = useState<string>('');
    const [lastHeartbeatAge, setLastHeartbeatAge] = useState<number | null>(null);
    const [locksActiveCount, setLocksActiveCount] = useState<number>(0);
    const [sparklineHistory, setSparklineHistory] = useState<{ q: number, r: number, f: number }[]>([]);

    const [runningId, setRunningId] = useState<string | null>(null); // 'NEXT', 'DRAIN', 'UNSTICK', or jobId
    const [globalStatus, setGlobalStatus] = useState('');

    // Ops Controls State
    const [refreshIntervalEnv, setRefreshIntervalEnv] = useState<number>(5000);
    const [isPaused, setIsPaused] = useState(false);

    // Play Latest State
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewTitle, setPreviewTitle] = useState('');

    // Modals
    const [showDrainModal, setShowDrainModal] = useState(false);
    const [drainMaxJobs, setDrainMaxJobs] = useState(5);
    const [drainMaxSeconds, setDrainMaxSeconds] = useState(20);

    const [showUnstickModal, setShowUnstickModal] = useState(false);
    const [unstickSeconds, setUnstickSeconds] = useState(600);

    // Artifact Drawer State
    const [artifacts, setArtifacts] = useState<Record<string, any>>({});

    // Inspector State
    const [showInspector, setShowInspector] = useState(false);
    const [inspectorData, setInspectorData] = useState<any | null>(null);
    const [duplicateJob, setDuplicateJob] = useState<any | null>(null); // New State
    const [pendingCookParams, setPendingCookParams] = useState<any | null>(null); // New State
    const [inspectorLoading, setInspectorLoading] = useState(false);

    const refresh = () => adminFetch('/api/factory/queue/status')
        .then(r => r.json())
        .then(d => {
            if (d.ok) {
                // Merge status API data with expected queue shape if needed, 
                // but usually we want to call /api/factory/queue for the full list if 'queue.jobs' is missing.
                // The status endpoint returns 'counts' and 'runningPreview', but maybe not the full list.
                // Wait, previous code called /api/factory/queue which returns full list.
                // Let's call BOTH or stick to /api/factory/queue if that's what renders the list.
                // Actually, the requirements say "Ops HUD" needs heartbeat/counts.
                // And we need the list for cards.
                // Let's call /api/factory/queue for the List, AND /api/factory/queue/status for the HUD.
                // To minimize races, we can chain them.
                setHeartbeat(d.heartbeat);
                setCounts(d.counts);
                setDeployTag(d.deployTag);
                setLastHeartbeatAge(d.lastHeartbeatAgeSeconds);
                setLocksActiveCount(d.locksActiveCount || 0);

                setSparklineHistory(prev => {
                    const snap = { q: d.counts.queued || 0, r: d.counts.running || 0, f: d.counts.failed || 0 };
                    const next = [...prev, snap];
                    return next.slice(-60); // Apply limit
                });

                return adminFetch('/api/factory/queue');
            }
            throw new Error(d.error || "Status fetch failed");
        })
        .then(r => r.json())
        .then(d => setQueue(d.queue))
        .catch((e) => console.warn("Queue fetch error", e));

    useEffect(() => {
        refresh();
        const i = setInterval(() => {
            if (!isPaused) refresh();
        }, refreshIntervalEnv);

        // Slow artifact refresh
        const j = setInterval(() => {
            if (!isPaused) {
                setArtifacts(prev => {
                    Object.keys(prev).forEach(jid => {
                        if (prev[jid].open) toggleArtifacts(jid, true);
                    });
                    return prev;
                });
            }
        }, 5000);

        return () => { clearInterval(i); clearInterval(j); };
    }, [refreshIntervalEnv, isPaused]);


    // ACTIONS
    const runInspect = async (jobId: string) => {
        setIsPaused(true); // Pause refreshing so we don't jump around
        setShowInspector(true);
        setInspectorLoading(true);
        try {
            const r = await adminFetch(`/api/factory/jobs/${jobId}`);
            const d = await r.json();
            if (d.ok) setInspectorData(d);
            else setInspectorData({ error: d.error });
        } catch (e: any) {
            setInspectorData({ error: e.message });
        }
        setInspectorLoading(false);
    };

    const runUnstickLock = async (bookId: string) => {
        if (!confirm(`Force Unstick Lock for ${bookId}?`)) return;
        try {
            const r = await adminFetch('/api/factory/queue/unstick_lock', {
                method: 'POST',
                body: JSON.stringify({ bookId })
            });
            const d = await r.json();
            if (d.ok) {
                alert(`Unstuck! Deleted: ${d.deleted}\nKey: ${d.key}`);
                // Refresh inspector if open
                if (inspectorData && inspectorData.job?.params?.id === bookId) {
                    runInspect(inspectorData.job.id);
                }
            } else {
                alert(`Error: ${d.error}`);
            }
        } catch (e: any) { alert(e.message); }
    };

    const runDrain = async () => {
        if (!confirm(`Run DRAIN? Max ${drainMaxJobs} jobs / ${drainMaxSeconds}s`)) return;
        setRunningId('DRAIN');
        setGlobalStatus("Draining...");
        setShowDrainModal(false);
        try {
            const r = await adminFetch('/api/factory/queue/drain', {
                method: 'POST',
                body: JSON.stringify({ maxJobs: drainMaxJobs, maxSeconds: drainMaxSeconds })
            });
            const d = await r.json();
            if (d.ok) {
                setGlobalStatus(`Drain Complete: ${d.processed} proc, ${d.succeeded} ok, ${d.failed} fail (${d.stopReason})`);
                refresh();
            } else { setGlobalStatus(`Drain Error: ${d.error}`); }
        } catch (e) { setGlobalStatus(`Net Error: ${e}`); }
        setRunningId(null);
    };

    const runUnstick = async () => {
        if (!confirm(`Unstick jobs older than ${unstickSeconds}s?`)) return;
        setRunningId('UNSTICK');
        setGlobalStatus("Unsticking...");
        setShowUnstickModal(false);
        try {
            const r = await adminFetch('/api/factory/queue/unstick', {
                method: 'POST',
                body: JSON.stringify({ olderThanSeconds: unstickSeconds })
            });
            const d = await r.json();
            if (d.ok) {
                setGlobalStatus(`Unstuck ${d.unstuckCount} jobs: ${d.jobIds.join(', ')}`);
                refresh();
            } else { setGlobalStatus(`Unstick Error: ${d.error}`); }
        } catch (e) { setGlobalStatus(`Net Error: ${e}`); }
        setRunningId(null);
    };

    const runJobStep = async (jobId: string) => {
        // "Step (10)" or "Run Next"
        const isNext = !jobId;
        setRunningId(isNext ? 'NEXT' : jobId);
        setGlobalStatus(isNext ? "Running Next..." : "Stepping Job...");
        try {
            const url = isNext ? '/api/factory/queue/run_next' : '/api/factory/run_job';
            const body = isNext ? {} : { jobId };
            const r = await adminFetch(url, { method: 'POST', body: JSON.stringify(body) });
            const d = await r.json();
            if (d.ok) {
                setGlobalStatus(`Success: ${d.jobId || d.job?.id} (${d.result?.status})`);
                refresh();
            } else { setGlobalStatus(`Error: ${d.error}`); }
        } catch (e) { setGlobalStatus(`Net Error: ${e}`); }
        setRunningId(null);
    };

    const runAutopilot = async (jobId: string) => {
        if (!confirm(`Start Autopilot (Background) for ${jobId}?`)) return;
        setRunningId(jobId);
        setGlobalStatus("Triggering Autopilot...");
        try {
            const r = await adminFetch(`/api/factory/jobs/${jobId}/run`, { method: 'POST' });
            const d = await r.json();
            if (d.ok) {
                setGlobalStatus(`Autopilot Enqueued for ${jobId}`);
                refresh();
            } else { alert(d.error); setGlobalStatus(`Error: ${d.error}`); }
        } catch (e) { alert(e); }
        setRunningId(null);
    };

    const runBookJob = async (bookId: string) => {
        if (!confirm(`Warning: This will KICK/RESTART the job for ${bookId}.\n\nIt will:\n1. Re-process text chunks\n2. Re-create playlist\n3. Seed Chunk 0 to the Queue (creating a new job if missing)\n\nUse this only if the job is stuck and not moving.`)) return;
        setRunningId(bookId);
        setGlobalStatus(`Kicking job ${bookId}...`);
        try {
            const r = await adminFetch('/api/factory/queue/run_book', {
                method: 'POST',
                body: JSON.stringify({ bookId })
            });
            const d = await r.json();
            if (d.ok) {
                alert(`Job Restarted/Kicked!\nResult: ${JSON.stringify(d.result, null, 2)}`);
                refresh();
                // Refresh inspector if open
                if (inspectorData && inspectorData.job?.params?.id === bookId) {
                    runInspect(d.result?.jobId || inspectorData.job.id);
                }
            } else {
                alert(`Error: ${d.error}`);
            }
        } catch (e: any) { alert(e.message); }
        setGlobalStatus('');
        setRunningId(null);
    };

    const controlJob = async (jobId: string, action: 'pause' | 'resume' | 'retry' | 'skip') => {
        if (!confirm(`${action.toUpperCase()} Job ${jobId}?`)) return;
        try {
            const r = await adminFetch(`/api/factory/job/${jobId}/control`, { method: 'POST', body: JSON.stringify({ action }) });
            if (r.ok) { refresh(); setGlobalStatus(`Action ${action} OK`); }
            else { alert("Control Failed"); }
        } catch (e) { alert(e); }
    };

    const toggleArtifacts = async (jobId: string, forceRefresh = false) => {
        let shouldFetch = forceRefresh;
        if (!forceRefresh) {
            const currentlyOpen = artifacts[jobId]?.open;
            if (currentlyOpen) {
                setArtifacts(prev => { const c = { ...prev }; delete c[jobId]; return c; });
                return;
            } else {
                setArtifacts(prev => ({ ...prev, [jobId]: { open: true, loading: true, items: [] } }));
                shouldFetch = true;
            }
        }
        if (!shouldFetch) return;
        try {
            const r = await adminFetch(`/api/factory/job/${jobId}/artifacts`);
            const d = await r.json();
            if (d.ok) {
                setArtifacts(prev => {
                    if (!forceRefresh && !prev[jobId]?.open) return prev;
                    return { ...prev, [jobId]: { open: true, loading: false, items: d.artifacts } };
                });
            }
        } catch (e) { console.error(e); }
    };

    const playJobLatest = async (jobId: string) => {
        try {
            // JIT Fetch
            const r = await adminFetch(`/api/factory/job/${jobId}/artifacts`);
            const d = await r.json();
            if (d.ok && d.artifacts && Array.isArray(d.artifacts)) {
                const mp3s = d.artifacts.filter((a: any) => a.key.endsWith('.mp3'));
                if (mp3s.length === 0) { alert("No MP3s found."); return; }

                // Sort DESC numeric
                mp3s.sort((a: any, b: any) => {
                    const na = parseInt(a.key.split('/').pop()?.match(/(\d+)/)?.[0] || '-1');
                    const nb = parseInt(b.key.split('/').pop()?.match(/(\d+)/)?.[0] || '-1');
                    return nb - na;
                });

                const latest = mp3s[0];
                setPreviewUrl(latest.url || `/api/library/artifact/${jobId}/${latest.key.split('/').pop()}`);
                setPreviewTitle(`Latest: ${latest.key.split('/').pop()}`);
            } else {
                alert("Could not load artifacts.");
            }
        } catch (e) { alert("Fetch error"); }
    };

    const timeAgo = (iso?: string) => {
        if (!iso) return '-';
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        return `${Math.floor(s / 3600)}h ago`;
    };

    // Helper: Stalled?
    const isStalled = (job: any) => {
        if (job.status !== 'processing' && job.status !== 'running') return false;
        const ms = Date.now() - new Date(job.updatedAt || job.startedAt || job.submittedAt).getTime();
        return ms > 600 * 1000; // 10 mins warning
    };

    if (mini) {
        // ... mini view remains similar but simpler ...
        return <div style={{ padding: 10, color: '#aaa' }}>Mini View Not Updated</div>;
    }

    return (
        <div style={{ background: '#111', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* 1. OPS HUD (Sticky) */}
            <div style={{
                background: '#222', borderBottom: '1px solid #444', padding: '10px 15px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px'
            }}>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', fontSize: '12px' }}>
                    <Tooltip content="Hover/long-press any control for details. Technical specs included." side="right">
                        <div style={{ cursor: 'help', color: '#aaa', border: '1px solid #444', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>?</div>
                    </Tooltip>
                    <div title="Deploy Tag" style={{ fontFamily: 'monospace', color: '#81c784' }}>{deployTag || '...'}</div>

                    <div style={{ display: 'flex', gap: '5px' }}>
                        <span style={{ color: '#90caf9' }}>Q: {counts.queued || 0}</span>
                        <span style={{ color: '#ffcc80' }}>R: {counts.running || 0}</span>
                        <span style={{ color: '#ef5350' }}>F: {counts.failed || 0}</span>
                        <span style={{ color: '#bdbdbd', marginLeft: 5 }} title="Active Locks">🔒 {locksActiveCount}</span>
                    </div>

                    {/* SPARKLINE */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', height: '20px', gap: '1px', marginLeft: 5, opacity: 0.8 }} title="History (60 ticks)">
                        {sparklineHistory.map((s, i) => {
                            const hF = Math.min(10, s.f * 2);
                            const hR = Math.min(10, s.r * 2);
                            const hQ = Math.min(10, Math.max(1, s.q * 0.5));
                            // Stacked bars: F on top, then R, then Q (all relative to bottom? No, simple bars)
                            // Let's do a single bar with fail color if fail > 0, else running, else queued
                            let bg = '#444';
                            let h = 2;
                            if (s.f > 0) { bg = '#ef5350'; h = hF; }
                            else if (s.r > 0) { bg = '#ffcc80'; h = hR; }
                            else if (s.q > 0) { bg = '#90caf9'; h = hQ; }
                            return <div key={i} style={{ width: '2px', height: `${h}px`, background: bg }} />;
                        })}
                    </div>

                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '5px',
                        color: (lastHeartbeatAge && lastHeartbeatAge > 60) ? '#ff5252' : '#69f0ae', fontWeight: 'bold'
                    }}>
                        ♥ {(lastHeartbeatAge !== null) ? `${lastHeartbeatAge}s` : '-'}
                    </div>

                    <div style={{ borderLeft: '1px solid #444', paddingLeft: '10px', color: '#aaa', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {heartbeat?.lastJobId ? `Last: ${heartbeat.lastJobId} (${heartbeat.lastJobStatus})` : 'No Recent Job'}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>

                    <div style={{ fontSize: '11px', display: 'flex', gap: '5px', background: '#333', padding: '2px', borderRadius: '4px' }}>
                        <button onClick={() => setIsPaused(!isPaused)} style={{ background: isPaused ? '#ef5350' : 'transparent', color: isPaused ? 'white' : '#aaa', border: 'none', cursor: 'pointer' }}>
                            {isPaused ? '❚❚' : '▶'}
                        </button>
                        {[2000, 5000, 10000].map(ms => (
                            <button key={ms} onClick={() => setRefreshIntervalEnv(ms)}
                                style={{ background: (!isPaused && refreshIntervalEnv === ms) ? '#666' : 'transparent', color: '#eee', border: 'none', cursor: 'pointer' }}>
                                {ms / 1000}s
                            </button>
                        ))}
                    </div>

                    <Tooltip content="Show Unstick Modal (Clear stale locks)" side="bottom">
                        <button onClick={() => setShowUnstickModal(true)} disabled={!!runningId} style={{ background: '#5d4037', color: '#ffab91', border: '1px solid #ffab91', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                            Unstick
                        </button>
                    </Tooltip>
                    <Tooltip content="Show Drain Modal (Run many jobs fast)" side="bottom">
                        <button onClick={() => setShowDrainModal(true)} disabled={!!runningId} style={{ background: '#e65100', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                            Drain
                        </button>
                    </Tooltip>
                    <Tooltip content="Run exactly one job step (synchronously). Calls: POST /api/factory/queue/run_next" side="bottom">
                        <button onClick={() => runJobStep('')} disabled={!!runningId} style={{ background: '#2e7d32', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                            {runningId === 'NEXT' ? 'Running...' : 'Run Next Step'}
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* STATUS BAR */}
            {globalStatus && <div style={{ background: '#333', color: '#fff', padding: '5px 15px', fontSize: '11px', borderBottom: '1px solid #444' }}>{globalStatus}</div>}

            {/* MODALS */}
            {showDrainModal && (
                <div style={{ position: 'absolute', top: 50, right: 20, width: 300, background: '#333', border: '1px solid #555', padding: 15, borderRadius: 8, zIndex: 100, boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>Drain Queue</h4>
                    <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>Max Jobs: {drainMaxJobs}</label>
                        <input type="range" min="1" max="50" value={drainMaxJobs} onChange={e => setDrainMaxJobs(Number(e.target.value))} style={{ width: '100%' }} />
                    </div>
                    <div style={{ marginBottom: 15 }}>
                        <label style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>Max Seconds: {drainMaxSeconds}s</label>
                        <input type="range" min="10" max="120" value={drainMaxSeconds} onChange={e => setDrainMaxSeconds(Number(e.target.value))} style={{ width: '100%' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button onClick={() => setShowDrainModal(false)} style={{ background: 'transparent', border: '1px solid #555', color: '#ccc', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                        <Tooltip content="DANGER: Runs many jobs in loop. Calls: POST /api/factory/queue/drain" side="top">
                            <button onClick={runDrain} style={{ background: '#e65100', color: 'white', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>START DRAIN</button>
                        </Tooltip>
                    </div>
                </div>
            )}

            {showUnstickModal && (
                <div style={{ position: 'absolute', top: 50, right: 80, width: 280, background: '#333', border: '1px solid #555', padding: 15, borderRadius: 8, zIndex: 100, boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>Unstick Stale Jobs</h4>
                    <div style={{ marginBottom: 15 }}>
                        <label style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>Older Than: {unstickSeconds}s</label>
                        <input type="range" min="60" max="3600" step="60" value={unstickSeconds} onChange={e => setUnstickSeconds(Number(e.target.value))} style={{ width: '100%' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button onClick={() => setShowUnstickModal(false)} style={{ background: 'transparent', border: '1px solid #555', color: '#ccc', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                        <Tooltip content="Steals locks from stale jobs. Calls: POST /api/factory/queue/unstick" side="top">
                            <button onClick={runUnstick} style={{ background: '#5d4037', color: '#ffab91', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>UNSTICK</button>
                        </Tooltip>
                    </div>
                </div>
            )}

            {/* INSPECTOR MODAL */}
            {showInspector && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ width: '80%', height: '80%', background: '#222', border: '1px solid #444', borderRadius: 8, display: 'flex', flexDirection: 'column', padding: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <h3 style={{ margin: 0 }}>Job Inspector</h3>
                            <button onClick={() => setShowInspector(false)} style={{ background: 'transparent', border: 'none', color: '#aaa', fontSize: 20, cursor: 'pointer' }}>×</button>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', fontFamily: 'monospace', color: '#ccc' }}>
                            {inspectorLoading && <div>Loading...</div>}
                            {!inspectorLoading && inspectorData && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                    <div>
                                        {/* COMPUTED PANEL */}
                                        <h4 style={{ color: '#81c784', borderBottom: '1px solid #333', paddingBottom: 5 }}>Computed State</h4>

                                        {inspectorData.computed ? (
                                            <div style={{ fontSize: 13, lineHeight: '1.6em' }}>
                                                <div>Lock Status:
                                                    {inspectorData.computed.lockInfo ?
                                                        <span style={{ color: '#ef5350', fontWeight: 'bold' }}>
                                                            LOCKED by {inspectorData.computed.lockInfo.owner} ({inspectorData.computed.lockInfo.ageSeconds}s ago) {' '}
                                                            <Tooltip content="Force delete lock in KV. Calls: POST .../unstick_lock" side="top">
                                                                <button
                                                                    onClick={() => runUnstickLock(inspectorData.job?.params?.id)}
                                                                    style={{ padding: '2px 5px', fontSize: 10, background: '#c62828', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', marginLeft: 5 }}
                                                                >
                                                                    UNSTICK LOCK
                                                                </button>
                                                            </Tooltip>
                                                        </span> :
                                                        <span style={{ color: '#66bb6a' }}> FREE</span>
                                                    }
                                                </div>

                                                <div style={{ marginTop: 15, marginBottom: 15 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                                                        <span>Progress</span>
                                                        <span>{inspectorData.computed.producedChunks} / {inspectorData.job?.result?.chunkCount || '?'}</span>
                                                    </div>
                                                    <div style={{ width: '100%', height: 8, background: '#333', borderRadius: 4, overflow: 'hidden' }}>
                                                        <div style={{
                                                            width: `${Math.min(100, (inspectorData.computed.producedChunks / (inspectorData.job?.result?.chunkCount || 1)) * 100)}%`,
                                                            height: '100%',
                                                            background: '#4caf50',
                                                            transition: 'width 0.3s'
                                                        }} />
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', gap: 20 }}>
                                                    <div>
                                                        <div style={{ fontSize: 11, color: '#aaa' }}>LAST OUTPUT</div>
                                                        <div style={{ fontSize: 16 }}>
                                                            {inspectorData.computed.lastChunkAt ? timeAgo(inspectorData.computed.lastChunkAt) : 'Never'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: 11, color: '#aaa' }}>PACE (Est.)</div>
                                                        <div style={{ fontSize: 16, color: '#29b6f6' }}>
                                                            {(() => {
                                                                const recent = inspectorData.computed.recentChunks || [];
                                                                if (recent.length < 2) return '~';
                                                                // Calc avg interval
                                                                let totalDelta = 0;
                                                                for (let i = 0; i < recent.length - 1; i++) {
                                                                    const a = new Date(recent[i]).getTime();
                                                                    const b = new Date(recent[i + 1]).getTime();
                                                                    totalDelta += Math.abs(a - b);
                                                                }
                                                                const avgMs = totalDelta / (recent.length - 1);
                                                                if (avgMs === 0) return '~';
                                                                const cpm = (60000 / avgMs).toFixed(1);
                                                                return `${cpm} chunks/min`;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ marginTop: 15 }}>Next Index: <b>{inspectorData.computed.nextProcessingIndex}</b></div>
                                                <div>Attempts: <b>{inspectorData.job?.attempts || 0}</b></div>
                                                <div>Last Status Update: <b>{timeAgo(inspectorData.job?.updatedAt)}</b></div>

                                                <div style={{ marginTop: 20, borderTop: '1px solid #333', paddingTop: 10 }}>
                                                    <h4 style={{ color: '#ffcc80', margin: '0 0 10px 0', fontSize: 12 }}>Job Recovery</h4>
                                                    <Tooltip content="Re-seeds the job to the queue. Calls: POST /api/factory/queue/run_book" side="top">
                                                        <button
                                                            onClick={() => runBookJob(inspectorData.job?.params?.id)}
                                                            style={{
                                                                background: '#e65100', color: 'white', border: 'none',
                                                                padding: '6px 12px', borderRadius: 4, cursor: 'pointer',
                                                                fontWeight: 'bold', fontSize: 11
                                                            }}
                                                            title="" // Removed title in favor of Tooltip
                                                        >
                                                            KICK / RESTART JOB
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        ) : <div>No computed data</div>}

                                        {inspectorData.error && <div style={{ color: 'red', marginTop: 10 }}>Error: {inspectorData.error}</div>}
                                    </div>

                                    <div style={{ borderLeft: '1px solid #333', paddingLeft: 20 }}>
                                        {/* RAW JSON */}
                                        <h4 style={{ color: '#90caf9', borderBottom: '1px solid #333', paddingBottom: 5 }}>Raw Job JSON</h4>
                                        <pre style={{ fontSize: 11, background: '#111', padding: 10, borderRadius: 4, overflowX: 'auto' }}>
                                            {JSON.stringify(inspectorData.job || inspectorData, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}


            {/* JOB LIST */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(!queue || !queue.jobs || queue.jobs.length === 0) && <div style={{ color: '#666', textAlign: 'center', marginTop: 50 }}>Queue is Empty</div>}

                {queue?.jobs?.slice().reverse().filter((j: any) => j && j.id).map((j: any) => {
                    const artState = artifacts[j.id];
                    const age = timeAgo(j.updatedAt || j.startedAt || j.submittedAt);
                    const stalled = (j.status === 'running' || j.status === 'processing') && isStalled(j);
                    const artCount = artState?.items ? artState.items.filter((x: any) => x.key.endsWith('.mp3')).length : 0;
                    const expected = j.result?.chunkCount || 0;
                    const isReady = expected > 0 && artCount >= expected;

                    return (
                        <div key={j.id} style={{
                            background: '#252525', borderRadius: '4px', border: '1px solid #333',
                            borderLeft: `4px solid ${j.status === 'done' ? '#4caf50' : j.status === 'failed' ? '#f44336' : j.status === 'running' || j.status === 'processing' ? '#ff9800' : '#9e9e9e'}`
                        }}>
                            {/* HEADER */}
                            <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontWeight: 'bold', fontSize: '13px' }}>{j.params?.title || 'Untitled'}</span>
                                    <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#888' }}>{j.id}</span>
                                    {stalled && <span style={{ background: '#c62828', color: 'white', fontSize: '10px', padding: '1px 4px', borderRadius: '2px' }}>STALLED</span>}
                                    {j.status === 'running' && <span style={{ color: '#ff9800', fontSize: '10px', fontWeight: 'bold' }}>RUNNING</span>}
                                </div>
                                <div style={{ fontSize: '11px', color: '#aaa' }}>
                                    {age} • {j.status.toUpperCase()}
                                </div>
                            </div>

                            {/* BODY */}
                            <div style={{ padding: '8px 12px', fontSize: '12px', color: '#ccc' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                    <span>Mode: <b>{j.params?.mode || 'full'}</b></span>
                                    <span>Voice: <b>{j.params?.voice}</b></span>
                                    {j.result?.chunkCount > 0 && <span>Chunks: <b>{j.result.chunkCount}</b></span>}
                                    {j.attempts > 0 && <span style={{ color: '#ffcc80' }}>Attempts: {j.attempts}</span>}
                                    {j.step && <span>Step: {j.step}</span>}
                                </div>

                                {j.lastError && (
                                    <div style={{ background: 'rgba(255,0,0,0.1)', color: '#ef5350', padding: '4px', marginTop: '6px', borderRadius: '2px' }}>
                                        {j.lastError}
                                    </div>
                                )}
                            </div>

                            {/* FOOTER ACTIONS */}
                            <div style={{ padding: '8px 12px', background: '#222', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {/* Files */}
                                <Tooltip content="View generated artifacts (MP3, JSON)" side="top">
                                    <button onClick={() => toggleArtifacts(j.id)}
                                        style={{ fontSize: '11px', padding: '4px 8px', background: '#444', border: 'none', color: '#eee', borderRadius: '3px', cursor: 'pointer' }}>
                                        📦 Artifacts {artState?.open ? '▲' : '▼'} {artState?.loading ? '...' : ''}
                                    </button>
                                </Tooltip>

                                <Tooltip content="Open Job Inspector Modal" side="top">
                                    <button onClick={() => runInspect(j.id)}
                                        style={{ fontSize: '11px', padding: '4px 8px', background: '#555', border: 'none', color: '#eee', borderRadius: '3px', cursor: 'pointer' }}>
                                        🔍 Inspect
                                    </button>
                                </Tooltip>

                                <Tooltip content="Play most recent MP3 chunk" side="top">
                                    <button onClick={() => playJobLatest(j.id)}
                                        style={{ fontSize: '11px', padding: '4px 8px', background: '#2e7d32', border: 'none', color: '#white', borderRadius: '3px', cursor: 'pointer' }}>
                                        ▶ Latest
                                    </button>
                                </Tooltip>

                                {/* Autopilot / Run */}
                                {(j.status === 'queued' || j.status === 'paused' || j.status === 'failed') && (
                                    <>
                                        <Tooltip content="Start background worker. Calls: POST .../run" side="top">
                                            <button onClick={() => runAutopilot(j.id)}
                                                style={{ fontSize: '11px', padding: '4px 8px', background: '#1565c0', border: 'none', color: 'white', borderRadius: '3px', cursor: 'pointer' }}>
                                                🚀 Autopilot (BG)
                                            </button>
                                        </Tooltip>
                                        <Tooltip content="Run 10 chunks synchronously. Calls: POST .../run_job" side="top">
                                            <button onClick={() => runJobStep(j.id)}
                                                style={{ fontSize: '11px', padding: '4px 8px', background: '#2e7d32', border: 'none', color: 'white', borderRadius: '3px', cursor: 'pointer' }}>
                                                ▶ Step (10)
                                            </button>
                                        </Tooltip>
                                    </>
                                )}

                                {/* Publish */}
                                {(j.status === 'done' || j.status === 'processing' || j.status === 'queued') && (
                                    <Tooltip content="Generate and publish playlist.json. Calls: POST .../publish" side="top">
                                        <button onClick={async () => {
                                            if (!confirm("Publish Playlist?")) return;
                                            try { await adminFetch(`/api/factory/jobs/${j.id}/publish`, { method: 'POST' }); alert("Published"); refresh(); } catch (e: any) { alert(e.message); }
                                        }}
                                            style={{ fontSize: '11px', padding: '4px 8px', background: isReady ? '#00e676' : '#00695c', border: 'none', color: isReady ? 'black' : 'white', borderRadius: '3px', cursor: 'pointer' }}>
                                            🚀 Publish
                                        </button>
                                    </Tooltip>
                                )}

                                <div style={{ flex: 1 }}></div>

                                {/* Controls */}
                                {j.status === 'running' && (
                                    <Tooltip content="Pause job execution" side="top">
                                        <button onClick={() => controlJob(j.id, 'pause')} style={{ fontSize: '11px', padding: '4px 8px', background: '#fbc02d', color: 'black', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>⏸ Pause</button>
                                    </Tooltip>
                                )}
                                {(j.status === 'failed' || j.status === 'paused') && (
                                    <Tooltip content="Retry failed/paused job" side="top">
                                        <button onClick={() => controlJob(j.id, 'retry')} style={{ fontSize: '11px', padding: '4px 8px', background: '#ff9800', color: 'black', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>🔄 Retry</button>
                                    </Tooltip>
                                )}
                                {(j.status !== 'done') && (
                                    <Tooltip content="Skip current step" side="top">
                                        <button onClick={() => controlJob(j.id, 'skip')} style={{ fontSize: '11px', padding: '4px 8px', background: '#424242', color: '#aaa', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>⏭ Skip</button>
                                    </Tooltip>
                                )}
                                <Tooltip content="Delete job. Destructive!" side="top">
                                    <button onClick={async () => {
                                        if (!confirm("Delete Job?")) return;
                                        await adminFetch(`/api/factory/jobs/${j.id}`, { method: 'DELETE' });
                                        refresh();
                                    }} style={{ fontSize: '11px', padding: '4px 8px', background: '#c62828', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>🗑 Delete</button>
                                </Tooltip>
                            </div>

                            {/* DRAWER */}
                            {artState?.open && (
                                <div style={{ background: '#111', padding: '10px', borderTop: '1px solid #333', maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                                    {artState.loading && <div style={{ color: '#888', fontStyle: 'italic' }}>Loading artifacts...</div>}
                                    {!artState.loading && artState.items && (
                                        <ArtifactsList
                                            items={artState.items}
                                            id={j.id}
                                            filter={""}
                                            onFilterChange={() => { }} // No filter needed here yet, or easy to add
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>


            {/* DUPLICATE JOB MODAL */}
            {
                duplicateJob && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <div style={{
                            background: '#222', padding: 30, borderRadius: 12,
                            width: '90%', maxWidth: 500, border: '1px solid #444',
                            boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
                        }}>
                            <h2 style={{ marginTop: 0, color: '#ffcc00' }}>⚠️ Duplicate Job Detected</h2>
                            <p style={{ color: '#ccc', lineHeight: '1.5em' }}>
                                A job for <b>{duplicateJob.params.id}</b> is already in the system.
                            </p>

                            <div style={{ background: '#333', padding: 15, borderRadius: 6, marginBottom: 20 }}>
                                <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>EXISTING JOB</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span>Status:</span>
                                    <span style={{
                                        fontWeight: 'bold',
                                        color: duplicateJob.status === 'failed' ? '#ef5350' :
                                            duplicateJob.status === 'running' ? '#66bb6a' : '#fff'
                                    }}>{duplicateJob.status.toUpperCase()}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span>Step:</span>
                                    <span>{duplicateJob.step || 'Starting'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Updated:</span>
                                    <span>{timeAgo(duplicateJob.updatedAt || duplicateJob.submittedAt)}</span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <button
                                    onClick={() => { setDuplicateJob(null); setPendingCookParams(null); }}
                                    style={{ padding: '12px', background: '#444', border: 'none', color: '#fff', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    <b>Cancel</b> (Keep existing job)
                                </button>

                                <div style={{ textAlign: 'center', fontSize: 11, color: '#666', margin: '-5px 0' }}>
                                    (Selecting "Cancel" will still keep the new cover if you uploaded one)
                                </div>

                                <Tooltip content="Restart job with new params (keeps audio). Calls: POST .../cook" side="top">
                                    <button
                                        onClick={() => handleDuplicateResolve('replace')}
                                        style={{ padding: '12px', background: '#1976d2', border: 'none', color: '#fff', borderRadius: 6, cursor: 'pointer', marginTop: 10 }}
                                    >
                                        <b>Replace Job</b> (Restart from Queue)
                                        <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 'normal' }}>Updates parameters, clears errors. Keeps existing audio.</div>
                                    </button>
                                </Tooltip>

                                <Tooltip content="Hard Reset: Deletes all audio and restarts. Calls: POST .../cook" side="top">
                                    <button
                                        onClick={() => handleDuplicateResolve('reset')}
                                        style={{ padding: '12px', background: '#c62828', border: 'none', color: '#fff', borderRadius: 6, cursor: 'pointer', marginTop: 10 }}
                                    >
                                        <b>Delete EVERYTHING & Restart</b>
                                        <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 'normal' }}>Deletes ALL audio/playlists. Preserves Cover info.</div>
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                )
            }

            {previewUrl && (
                <AudioPlayerOverlay
                    url={previewUrl}
                    title={previewTitle}
                    onClose={() => setPreviewUrl(null)}
                />
            )}
        </div >
    );
};


const MaintenanceView = () => {
    const [latest, setLatest] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState("");
    const [query, setQuery] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);

    useEffect(() => { load(); }, []);
    const load = async () => { const r = await adminFetch('/api/factory/audit/latest'); if (r.ok) setLatest(await r.json()); };
    const runAudit = async () => { setLoading(true); const r = await adminFetch('/api/factory/audit', { method: 'POST' }); setResult(JSON.stringify(await r.json(), null, 2)); load(); setLoading(false); };

    const copyAuditJson = async () => {
        if (!result) return;
        try {
            await navigator.clipboard.writeText(result);
            alert("Copied audit JSON to clipboard ✓");
        } catch (err) {
            // Fallback
            const textArea = document.createElement("textarea");
            textArea.value = result;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                alert("Copied audit JSON to clipboard ✓");
            } catch (err2) {
                alert("Copy failed — try selecting and copying manually");
            }
            document.body.removeChild(textArea);
        }
    };

    const runFixAll = async () => {
        if (!confirm("Queue fix for all broken?")) return;

        let ids: string[] = [];
        if (latest?.summary?.brokenIds) {
            ids = latest.summary.brokenIds;
        } else {
            // Fallback or alert if we suspect checking remote
            // But usually we trust valid latest.
        }

        if (!ids || ids.length === 0) {
            alert("No broken IDs found in the latest audit summary.");
            return;
        }

        setLoading(true);
        const r = await adminFetch('/api/factory/fix-batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids, plan: 'fast' })
        });
        setResult(JSON.stringify(await r.json(), null, 2));
        setLoading(false);
    };
    const resolve = async () => { setLoading(true); const r = await adminFetch('/api/factory/resolve', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query }) }); const d = await r.json(); if (d.ok) setSearchResults(d.matches || []); setLoading(false); };
    const fixOne = async (id: string, plan: string, dry: boolean) => { setLoading(true); const r = await adminFetch('/api/factory/fix', { method: 'POST', body: JSON.stringify({ id, plan, dryRun: dry }) }); setResult(JSON.stringify(await r.json(), null, 2)); setLoading(false); };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: '20px', padding: '10px', background: '#222', borderBottom: '1px solid #333' }}>
                <div>
                    <h3>Audit</h3>
                    <div style={{ fontSize: '12px', color: '#aaa' }}>
                        Last: {latest?.summary?.generatedAt || 'Never'} <br />
                        Broken: {latest?.summary?.brokenCount || 0} / {latest?.summary?.totalBooks || 0} <br />
                        Style: v2_unified (gpt-image-1.5)
                    </div>
                    <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                        <Tooltip content="Run comprehensive audit query. Calls: POST /api/factory/audit" side="bottom">
                            <button onClick={runAudit} disabled={loading} style={{ padding: '5px 10px', background: '#1565c0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Run Audit</button>
                        </Tooltip>
                        <Tooltip content="Queue fixes for all broken items. Calls: POST /api/factory/fix-batch" side="bottom">
                            <button onClick={runFixAll} disabled={loading || !latest?.summary?.brokenCount} style={{ padding: '5px 10px', background: '#c62828', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Fix All Broken</button>
                        </Tooltip>
                        {result && (
                            <Btn
                                label="Copy JSON"
                                onClick={copyAuditJson}
                                icon={Icons.Copy}
                                help="Copy audit result to clipboard"
                                style={{ marginLeft: 10, background: '#555' }}
                            />
                        )}
                    </div>
                </div>
                <div style={{ flex: 1, marginLeft: '20px' }}>
                    <h3>Lookup / Repair</h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && resolve()} placeholder="Title or ID" style={{ padding: '5px', background: '#333', color: 'white', border: '1px solid #444', flex: 1 }} />
                        <button onClick={resolve} disabled={loading} style={{ padding: '5px 10px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px' }}>Search</button>
                    </div>
                </div>
            </div>
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '10px', borderRight: '1px solid #333' }}>
                    {searchResults.map((m: any, i: number) => (
                        <div key={i} style={{ marginBottom: '10px', background: '#222', padding: '10px', borderRadius: '4px' }}>
                            <div style={{ fontWeight: 'bold' }}>Query: {m.query}</div>
                            {m.resolved.map((r: any) => (
                                <div key={r.id} style={{ marginLeft: '10px', marginTop: '5px', padding: '5px', borderLeft: '3px solid #555' }}>
                                    <div>{r.title} <span style={{ color: '#666' }}>({r.id})</span></div>
                                    <div style={{ marginTop: '5px', display: 'flex', gap: '5px' }}>
                                        <Tooltip content="Check for issues without fixing. Calls: POST .../fix (dryRun)" side="right">
                                            <button onClick={() => fixOne(r.id, 'full', true)} style={{ fontSize: '10px', padding: '2px 5px' }}>Audit (Dry Run)</button>
                                        </Tooltip>
                                        <Tooltip content="Quick repair (metadata only). Calls: POST .../fix (plan=fast)" side="right">
                                            <button onClick={() => fixOne(r.id, 'fast', false)} style={{ fontSize: '10px', padding: '2px 5px', background: '#ef6c00', color: 'white', border: 'none' }}>Fix Fast</button>
                                        </Tooltip>
                                        <Tooltip content="Full repair (validation + metadata). Calls: POST .../fix (plan=full)" side="right">
                                            <button onClick={() => fixOne(r.id, 'full', false)} style={{ fontSize: '10px', padding: '2px 5px', background: '#c62828', color: 'white', border: 'none' }}>Fix Full</button>
                                        </Tooltip>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <pre style={{ flex: 1, overflow: 'auto', padding: '10px', fontSize: '11px', color: '#81c784', margin: 0 }}>{result}</pre>
            </div>
        </div>
    );
};


const StatusBadge = ({ label, ok }: any) => (<span style={{ padding: '2px 4px', borderRadius: '4px', background: ok ? '#1b5e20' : '#b71c1c', color: 'white', fontSize: '10px' }}>{label} {ok ? '✓' : '✗'}</span>);
